import classNames from "classnames";
import Downshift, { ControllerStateAndHelpers } from "downshift";
import { EmojiConvertor } from "emoji-js";
import "emoji-mart/css/emoji-mart.css";
import { template } from "lodash";
import { observer } from "mobx-react";
import React, { FormEvent, useEffect, useState } from "react";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { NotificationType } from "@fidget/common/models/mst/ui/ChatNotificationStore";
import {
  includesRegularExpression,
  convertExpressionToRegexString
} from "@fidget/common/utils/searchMatching";
import { useStore } from "../../models";
import { IIntentSuggestion, IntentResponseContext } from "../../models/intent/IntentStore";
import { InputType } from "../../models/room";
import { IChatInput } from "../../models/room/ChatInput";
import "../../styles/emoji-mart.css";
import styles from "./chatInputComponent.css";
import { ChatHighlighter } from "./chatInputUtils/chatHighlighter/chatHighlighter";
import { mentionsTemplates } from "./chatInputUtils/chatHighlighter/chatHighlighterTypes";
import { ChatMessageInput } from "./chatMessageInput";
import { IAttachment } from "@fidget/common/components/mstchat/chatMessage";
import { replaceText, templatePairs } from "../../../src/models/intent/intentStoreUtils";
import { staticConfig } from "@fidget/common/config/static-config";

interface Props {
  inputState: IChatInput;
  onMessage: (message: string, attachments?: ReadonlyArray<IAttachment>) => void;
  onNote: (message: string) => void;
  onMessageTyping: () => void;
  guestFirstName?: string;
}

export const filterIntentsUsingExpression = (
  intents: IIntentSuggestion[],
  expression: string
): IIntentSuggestion[] => {
  const regexStr = convertExpressionToRegexString(expression);

  return intents.filter((suggestion) => includesRegularExpression(suggestion.intent, regexStr));
};

export const ChatInputComponent = observer(
  ({ inputState, onMessage, onNote, onMessageTyping, guestFirstName }: Props) => {
    const { intentStore, currentUserStore, profanityStore, uiStore, roomStore } = useStore();
    const { suggestionsEnabled } = intentStore;
    const [textArea, setTextArea] = useState<HTMLTextAreaElement>();
    const [inputScrollRef, setInputScrollRef] = useState<HTMLDivElement>();
    const isIntentMenuOpened =
      !!suggestionsEnabled &&
      inputState.activeInputType === InputType.Message &&
      inputState.inputText.startsWith(staticConfig.commandActivationSequence);

    useEffect(() => {
      if (textArea) {
        textArea.focus();
      }
    }, [guestFirstName]);

    const addChatProfanityNotification = () => {
      uiStore.setChatNotification({
        message: intl.formatMessage("conversation.error.profanity.title"),
        type: NotificationType.ChatProfanity
      });
    };

    const handleSend = (e?: FormEvent) => {
      e?.preventDefault();
      const emoji = new EmojiConvertor();
      emoji.replace_mode = "unified";

      if (inputState.isReadyToSend) {
        if (currentUserStore.orgConfig?.profanitiesCheckingEnabled) {
          const maybeProfanity = profanityStore.matchProfanity(inputState.inputText);
          if (maybeProfanity) {
            addChatProfanityNotification();
            inputState.setFoundProfanity(maybeProfanity);
            return;
          }
        }

        // Temporary template needed
        const templateContext: IntentResponseContext = {
          agent: currentUserStore.profile,
          client: roomStore.currentRoom?.owner
        };
        const replacedText = replaceText(inputState.inputText, templatePairs, true);
        const compiledTemplate = template(replacedText)(templateContext);

        const trimmedText = emoji.replace_colons(compiledTemplate.trim());
        if (inputState.activeInputType === InputType.Note) {
          onNote(trimmedText);
        } else {
          onMessage(trimmedText, inputState.attachments as ReadonlyArray<IAttachment>);
        }
      }

      if (textArea) {
        textArea.focus();
      }
    };

    const handleIntentPicked = (
      intent: IIntentSuggestion | undefined,
      helpers: ControllerStateAndHelpers<IIntentSuggestion>
    ) => {
      if (!intent) {
        inputState.clearMessageInput();
        helpers.reset({ selectedItem: undefined });
        return;
      }

      fillInputFromIntent(intent, helpers).then(() => {
        if (inputScrollRef) {
          inputScrollRef.scrollTo({ top: inputScrollRef.scrollHeight, behavior: "smooth" });
        }
      });
    };

    const fillInputFromIntent = async (
      intent: IIntentSuggestion,
      helpers: ControllerStateAndHelpers<IIntentSuggestion>
    ) => {
      if (intent && inputState.activeInputType === InputType.Message) {
        const context: IntentResponseContext = {
          agent: currentUserStore.profile,
          client: roomStore.currentRoom?.owner
        };

        inputState.fillFromIntent(intent, context);
        helpers.reset({ selectedItem: undefined });
      }
    };

    const setTextAreaRef = (el?: HTMLTextAreaElement) => {
      if (el && !textArea) {
        setTextArea(el);
      }
    };

    const switchToTab = (type: InputType) => {
      if (textArea) {
        textArea.focus();
        setTextArea(undefined);
      }
      inputState.setInputType(type);
    };

    const getTabLabel = (type: InputType): string => {
      switch (type) {
        case InputType.Message:
          return intl.formatMessage("conversation.input.tab.message");
        case InputType.Note:
          return intl.formatMessage("conversation.input.tab.note");
        default:
          throw new Error("unsupported chat input tab: " + type);
      }
    };

    const renderTabs = () => {
      return (
        <div className={styles.tabs}>
          {Object.values(InputType).map((tab: InputType) => (
            <div
              key={tab}
              id={"chat_input" + tab}
              className={classNames(
                styles.tab,
                inputState.activeInputType === tab && styles.active
              )}
              onClick={() => switchToTab(tab)}
              onMouseDown={(ev) => ev.preventDefault()}
            >
              {getTabLabel(tab)}
            </div>
          ))}
        </div>
      );
    };

    const renderSendButton = () => {
      const { activeInputType, isReadyToSend, hasFiles } = inputState;
      const canAttachFiles = activeInputType === InputType.Message;
      const shouldShowFilesTooltip = canAttachFiles && hasFiles && !isReadyToSend;

      return (
        <TooltipComponent
          positionPreset={"top"}
          text={intl.formatMessage("conversation.input.disabled")}
          tooltipShow={shouldShowFilesTooltip}
          maxWidth={"200px"}
          width={"200px"}
        >
          <button
            className={classNames(styles.button, styles["in-tooltip"])}
            disabled={!isReadyToSend}
          >
            <L10n
              id={
                activeInputType === InputType.Message
                  ? "conversation.input.send"
                  : "conversation.input.save"
              }
            />
          </button>
        </TooltipComponent>
      );
    };

    const renderMessageInput = (helpers: ControllerStateAndHelpers<IIntentSuggestion>) => (
      <form onSubmit={handleSend}>
        <ChatMessageInput
          inputChatState={inputState}
          onMessageTyping={onMessageTyping}
          guestFirstName={guestFirstName}
          onSendMessage={handleSend}
          setTextAreaRef={setTextAreaRef}
          renderConfirmButtonElement={renderSendButton}
          inputProps={...helpers.getInputProps() as any}
          setInputScrollRef={setInputScrollRef}
          isIntentMenuOpened={isIntentMenuOpened}
          activationSequence={currentUserStore.orgConfig?.keyboardShortcutsEnabled ? staticConfig.mentionActivationSequence : undefined}
          delimeterSequence={staticConfig.mentionDelimeterSequence}
        />
      </form>
    );

    const lengthLimitString = (text: string): string => {
      return text.length > 100 ? text.substring(0, 100) + "..." : text;
    };

    const renderIntentListItem = (
      item: IIntentSuggestion,
      index: number,
      helpers: ControllerStateAndHelpers<IIntentSuggestion>
    ) => {
      const { getItemProps, highlightedIndex, selectedItem } = helpers;
      const highlighted = highlightedIndex === index;
      const selected = !!selectedItem && item.intent === selectedItem.intent;
      const itemProps = getItemProps({ key: index, index, item });

      const templateText = lengthLimitString(item.prettyTemplate ?? "");

      return (
        <li {...itemProps}>
          <div
            className={classNames(styles["default-grid"], {
              [styles.highlighted]: highlighted,
              [styles.selected]: selected
            })}
          >
            <div className={styles["item-intent"]}>{item.intent}</div>
            <div className={styles["item-description"]}>
              <ChatHighlighter text={templateText} parts={mentionsTemplates} />
            </div>
          </div>
        </li>
      );
    };

    const renderIntentListMenu = (helpers: ControllerStateAndHelpers<IIntentSuggestion>) => {
      const { getMenuProps } = helpers;
      const text = inputState.inputText.substring(1);
      const items = filterIntentsUsingExpression(
        intentStore.getActiveIntentSuggestions(
          roomStore.currentRoom?.languageOption || currentUserStore.currentLanguage
        ),
        text
      );

      return (
        <div className={styles["intent-list-container"]}>
          <div className={styles["menu-container"]}>
            {items.length ? (
              <ul {...getMenuProps()}>
                {items.map((item, index) => renderIntentListItem(item, index, helpers))}
              </ul>
            ) : (
              <div className={styles["no-results"]}>
                <i className={"icon-exclamation-mark"} />
                <p>{intl.formatMessage("ai.modelConfiguration.intents.searchNotFound")}</p>
              </div>
            )}
          </div>
        </div>
      );
    };

    const renderInputComponent = () => {
      return (
        <Downshift
          defaultHighlightedIndex={0}
          isOpen={isIntentMenuOpened}
          onChange={(intent, helpers) => handleIntentPicked(intent || undefined, helpers)}
        >
          {(helpers: ControllerStateAndHelpers<IIntentSuggestion>) => (
            <div>
              {renderMessageInput(helpers)}
              {helpers.isOpen && renderIntentListMenu(helpers)}
            </div>
          )}
        </Downshift>
      );
    };

    const render = () => (
      <div className={styles["chat-input"]}>
        {renderTabs()}
        {renderInputComponent()}
      </div>
    );

    return render();
  }
);
